import React from 'react';
import { isUndefined } from 'lodash';
import {
  isLossAversionCard,
  isModalCard,
} from '@bbkAdminUtils/api-client/rest-cards-types';
import {
  FunnelContext,
  isAdminFunnelSurface,
} from '@bbkFunnelComponents/FunnelContext';
import {
  getLossAversionDefinition,
  LossAversionCardFactory,
} from '@bbkFunnelComponents/loss-aversion-card-factory';
import ModalsFactory, {
  getModalDefinition,
} from '@bbkFunnelComponents/modals-factory';
import { RenderErrorBoundary } from '@bbkFunnelComponents/render-error-boundary';
import {
  CardRenderEnvironmentProvider,
  useCardRenderEnvironment,
  type CardRenderEnvironment,
} from './card-render-environment';

import '@bbkFunnelStylesheets/index.scss';

type ModalsFactoryProps = React.ComponentProps<typeof ModalsFactory>;
type LossAversionCardFactoryProps = React.ComponentProps<
  typeof LossAversionCardFactory
>;

type PreviewProps = {
  isPreview: true;
  card: LossAversionCardFactoryProps['card'] | ModalsFactoryProps['card'];
  isHorizontal?: boolean;
};
type Props = PreviewProps | ModalsFactoryProps | LossAversionCardFactoryProps;

export const getCardEditorHints = (card: Props['card']) =>
  isModalCard(card)
    ? getModalDefinition(card)?.editorHints
    : isLossAversionCard(card)
      ? getLossAversionDefinition(card)?.editorHints
      : undefined;

const isPreviewProps = (p: Props): p is PreviewProps =>
  !isUndefined((p as PreviewProps).isPreview);

const isLossAversionProps = (p: Props): p is LossAversionCardFactoryProps =>
  isLossAversionCard(p.card);

const isModalCardProps = (p: Props): p is ModalsFactoryProps =>
  isModalCard(p.card);

const CardContent: React.FC<Props> = (props) => {
  if (isPreviewProps(props)) {
    if (isLossAversionProps(props)) {
      return (
        <LossAversionCardFactory
          card={props.card}
          isHorizontal={props.isHorizontal}
        />
      );
    }
    if (isModalCardProps(props)) {
      return <ModalsFactory card={props.card} isPreview />;
    }
    return null;
  }

  if (isLossAversionProps(props)) {
    return <LossAversionCardFactory {...props} />;
  }
  if (isModalCardProps(props)) {
    return <ModalsFactory {...props} />;
  }
  return null;
};

/**
 * The dispatch lives in `CardContent` so that a renderer throwing while
 * matching or rendering a card is caught by the boundary rather than by
 * whatever mounted this factory.
 *
 * The preview prop alone does not identify the surface, because the designer
 * renders cards through `Loss` without it, so the context is consulted too.
 */
const CardFactory: React.FC<
  Props & {
    /** Omit to inherit the host environment; use previewCanvas: null to reset it. */
    environment?: CardRenderEnvironment;
  }
> = ({ environment, ...props }) => {
  const inheritedEnvironment = useCardRenderEnvironment();
  const funnelContext = React.useContext(FunnelContext);
  const { card } = props;

  return (
    <CardRenderEnvironmentProvider value={environment ?? inheritedEnvironment}>
      <RenderErrorBoundary
        label={`card ${card?.template} (${card?.name})`}
        resetKey={card}
        isAdminSurface={
          isPreviewProps(props) || isAdminFunnelSurface(funnelContext)
        }
      >
        <CardContent {...props} />
      </RenderErrorBoundary>
    </CardRenderEnvironmentProvider>
  );
};

export default CardFactory;
